
import { Button } from "@/components/ui/button";
import ReagentCard from "@/components/ReagentCard";
import { Reagent } from "@/types/reagent.types";
import { RefreshCw } from "lucide-react";
import { useEffect } from "react";
import { toast } from "sonner";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { dateToString } from "@/utils/dateUtils";

interface ReagentListProps {
  reagents: Reagent[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  resetFilters: () => void;
}

const ReagentList = ({ reagents, isLoading, error, refetch, resetFilters }: ReagentListProps) => {
  // Add effect to notify when reagents are loaded - only once
  useEffect(() => {
    if (!isLoading && reagents.length > 0) {
      console.log(`ReagentList successfully loaded ${reagents.length} reagents`);
    }
  }, [isLoading, reagents.length]);
  
  if (isLoading) {
    return (
      <div className="lg:col-span-3">
        <div className="bg-white p-6 rounded-lg shadow-sm text-center">
          <div className="flex flex-col items-center">
            <LoadingSpinner />
            <p className="text-gray-600 mt-4">Loading reagents...</p>
            <p className="text-gray-400 text-sm mt-2">This may take a few moments</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={refetch}
            >
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="lg:col-span-3">
        <div className="bg-white p-6 rounded-lg shadow-sm text-center">
          <h3 className="text-xl font-semibold mb-2">Error loading reagents</h3>
          <p className="text-red-500 mb-4">
            {error.message || "An error occurred while loading reagents."}
          </p>
          <Button
            onClick={() => {
              toast.info("Retrying to load reagents...");
              refetch();
            }}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" /> Try Again
          </Button>
        </div>
      </div>
    );
  }
  
  if (reagents.length === 0) {
    return (
      <div className="lg:col-span-3">
        <div className="bg-white p-6 rounded-lg shadow-sm text-center">
          <h3 className="text-xl font-semibold mb-2">No reagents found</h3>
          <p className="text-gray-600 mb-4">
            There are no reagents in the database, or they couldn't be loaded.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              variant="outline"
              onClick={resetFilters}
            >
              Clear Filters
            </Button>
            <Button
              onClick={() => {
                toast.info("Retrying to load reagents...");
                refetch();
              }}
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="lg:col-span-3">
      <div className="flex justify-between items-center mb-4">
        <p className="text-gray-600">
          Showing {reagents.length} reagents
        </p>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => {
            toast.info("Refreshing reagents...");
            refetch();
          }}
          className="flex items-center gap-1"
        >
          <RefreshCw className="h-3 w-3" /> Refresh
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
        {reagents.map((reagent) => (
          <ReagentCard
            key={reagent.id}
            id={reagent.id}
            name={reagent.name}
            catalogNumber={reagent.catalogNumber || reagent.catalogue_number || ""}
            category={reagent.category || (reagent.categories && reagent.categories.name) || "Uncategorized"}
            description={reagent.description || ""}
            amount={reagent.amount !== null ? 
              (reagent.amount_unit ? `${reagent.amount}${reagent.amount_unit}` : `${reagent.amount}`) 
              : "Not specified"}
            location={(reagent.profiles && reagent.profiles.location) || 
                    `${reagent.city || ""}, ${reagent.country || ""}`.trim() || ""}
            imageUrl={reagent.imageUrl || reagent.image_url || undefined}
            userName={(reagent.profiles && reagent.profiles.username) || reagent.userName || ""}
            labName={(reagent.profiles && reagent.profiles.lab) || reagent.labName || ""}
            numberOfPackages={reagent.numberOfPackages || reagent.number_of_packages || undefined}
            expiryDate={dateToString(reagent.expiryDate || reagent.expiry_date || null)}
          />
        ))}
      </div>
    </div>
  );
};

export default ReagentList;
